import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// ─── Constantes ──────────────────────────────────────────────────────────────

const HEALTH_CHECK_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const MIN_BETWEEN_CHECKS_MS = 30 * 1000; // 30 secondes anti-spam
const MAX_RESTART_ATTEMPTS = 2;
const RESTART_VERIFY_DELAY_MS = 12000; // delai avant re-verification apres restart (WAHA a besoin de temps)

// ─── Types ───────────────────────────────────────────────────────────────────

export interface WahaHealthStatus {
  checking: boolean;
  restarting: boolean;
  failed: boolean;
}

const HEALTH_STATUS_KEY = ["waha-health-status"];
const SESSION_KEY = ["waha-session"];

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Verifie periodiquement que la session WAHA est reellement active
 * (pas seulement en DB) et tente un redemarrage automatique si morte.
 *
 * Monte dans AppLayout a cote de useWahaRealtime().
 */
export function useWahaHealthCheck() {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { toast } = useToast();

  const lastCheckRef = useRef(0);
  const isCheckingRef = useRef(false);
  const restartAttemptsRef = useRef(0);

  const setHealthStatus = useCallback(
    (status: Partial<WahaHealthStatus>) => {
      const current =
        (queryClient.getQueryData<WahaHealthStatus>(HEALTH_STATUS_KEY)) ?? {
          checking: false,
          restarting: false,
          failed: false,
        };
      queryClient.setQueryData(HEALTH_STATUS_KEY, { ...current, ...status });
    },
    [queryClient],
  );

  const checkHealth = useCallback(async () => {
    // Guard : pages ou le check n'a pas de sens
    if (pathname === "/onboarding" || pathname === "/login") return;

    // Guard : pas de session du tout → rien a checker
    const session = queryClient.getQueryData<{ status: string } | null>(SESSION_KEY);
    if (!session) return;

    // Guard : anti-spam
    const now = Date.now();
    if (now - lastCheckRef.current < MIN_BETWEEN_CHECKS_MS) return;

    // Guard : pas d'appels concurrents
    if (isCheckingRef.current) return;

    isCheckingRef.current = true;
    lastCheckRef.current = now;
    setHealthStatus({ checking: true });

    try {
      // Verifier que la session Supabase est valide avant d'appeler l'Edge Function
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) {
        console.debug("[waha-health] No auth session, skipping check");
        return;
      }

      const { data, error } = await supabase.functions.invoke(
        "waha-session-manager",
        { body: { action: "status" } },
      );

      if (error) {
        // 401 = JWT expire → inutile de re-essayer en boucle
        if (error.message?.includes("non-2xx") || error.message?.includes("401")) {
          console.debug("[waha-health] Auth expired, pausing health checks");
          return;
        }
        console.warn("[waha-health] Status check failed:", error.message);
        return;
      }

      // Invalider le cache pour que les composants se mettent a jour
      await queryClient.invalidateQueries({ queryKey: SESSION_KEY });

      const realStatus = data?.status as string | undefined;
      const cachedStatus = session.status;

      // ── CAS 1 : WAHA dit WORKING → tout va bien ──────────────────
      if (realStatus === "WORKING") {
        restartAttemptsRef.current = 0;
        setHealthStatus({ checking: false, restarting: false, failed: false });
        return;
      }

      // ── CAS 2 : Session supprimee → rien a faire ─────────────────
      if (realStatus === "NOT_CONFIGURED") {
        setHealthStatus({ checking: false, restarting: false, failed: false });
        return;
      }

      // ── CAS 3 : WAHA dit STOPPED/FAILED ET le cache disait WORKING
      //    → C'est un crash, tenter un restart automatique ───────────
      if (
        cachedStatus === "WORKING" &&
        (realStatus === "STOPPED" || realStatus === "FAILED")
      ) {
        if (restartAttemptsRef.current >= MAX_RESTART_ATTEMPTS) {
          setHealthStatus({ checking: false, restarting: false, failed: true });
          toast({
            title: "WhatsApp déconnecté",
            description:
              "La reconnexion automatique a échoué. Rendez-vous dans Paramètres pour reconnecter.",
            variant: "destructive",
            duration: 10000,
          });
          return;
        }

        restartAttemptsRef.current += 1;
        setHealthStatus({ checking: false, restarting: true, failed: false });

        // Tenter le restart via action "create" (gere "already exists")
        const { error: restartError } = await supabase.functions.invoke(
          "waha-session-manager",
          { body: { action: "create" } },
        );

        if (restartError) {
          // 401 = JWT expire, pas la peine de marquer failed
          if (restartError.message?.includes("non-2xx") || restartError.message?.includes("401")) {
            console.debug("[waha-health] Auth expired during restart, skipping");
            setHealthStatus({ restarting: false });
            return;
          }
          console.warn("[waha-health] Restart failed:", restartError.message);
          setHealthStatus({ restarting: false, failed: true });
          return;
        }

        // Attendre que WAHA redemarre, puis re-verifier
        await new Promise((r) => setTimeout(r, RESTART_VERIFY_DELAY_MS));

        const { data: verifyData } = await supabase.functions.invoke(
          "waha-session-manager",
          { body: { action: "status" } },
        );

        await queryClient.invalidateQueries({ queryKey: SESSION_KEY });

        if (verifyData?.status === "WORKING") {
          restartAttemptsRef.current = 0;
          setHealthStatus({ checking: false, restarting: false, failed: false });
          toast({
            title: "WhatsApp reconnecté",
            description: "La session a été restaurée automatiquement.",
          });
        } else {
          setHealthStatus({ restarting: false });
          // Le prochain cycle retentera si attempts < MAX
        }
        return;
      }

      // ── CAS 4 : Status non-WORKING et cache non-WORKING ──────────
      // → Mode decouverte : le cache a ete rafraichi via invalidateQueries.
      //   Le Realtime prendra le relais si le statut change cote serveur.
    } catch (err) {
      console.warn("[waha-health] Unexpected error:", err);
    } finally {
      isCheckingRef.current = false;
      setHealthStatus({ checking: false });
    }
  }, [pathname, queryClient, setHealthStatus, toast]);

  // ── Verification initiale + intervalle periodique ─────────────────────────
  useEffect(() => {
    // Check initial apres un court delai (laisser le temps au cache de se remplir)
    const initialTimeout = setTimeout(checkHealth, 5000);

    const interval = setInterval(checkHealth, HEALTH_CHECK_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [checkHealth]);

  // ── Verification au retour sur l'onglet ───────────────────────────────────
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkHealth();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [checkHealth]);
}
